import { HStack, VStack } from 'components/layouts/primitives/Stack/Stack';
import { LayoutDashboardLeftMenuStyle as S } from './LayoutDashboardLeftMenu.style';
import { PButtonIcon } from 'components/prime/PButton/PButtonIcon';
import { useEffect, useMemo } from 'react';
import { useClassNames } from 'hooks/useClassNames';
import { Box } from 'components/layouts/primitives/Box/Box';
import { Divider } from 'components/Divider/Divider';
import { Text } from 'components/Text/Text';
import {
  LayoutDashboardLeftMenuItem,
  LeftMenuItem,
} from './components/LayoutDashboardLeftMenuItem/LayoutDashboardLeftMenuItem';
import { LayoutDashboardLeftMenuProjects } from './components/LayoutDashboardLeftMenuProjects/LayoutDashboardLeftMenuProjects';
import { LayoutDashboardLeftMenuSupport } from './components/LayoutDashboardLeftMenuSupport/LayoutDashboardLeftMenuSupport';
import { LayoutDashboardLeftMenuUser } from './components/LayoutDashboardLeftMenuUser/LayoutDashboardLeftMenuUser';
import { LayoutDashboardLeftMenuCustomers } from './components/LayoutDashboardLeftMenuCustomers/LayoutDashboardLeftMenuCustomers';
import { getFFStore } from 'stores/ff.store';
import { useObservableEagerState, useObservableState } from 'observable-hooks';
import { getUserStore } from 'stores/user.store';
import { getCustomerStore } from 'stores/customer.store';
import { getBillingPlanStore } from 'stores/billingPlan.store';
import { useLeftMenuOpen } from 'hooks/useLeftMenuOpen';
import { LayoutDashboardLeftMenuLogo } from './components/LayoutDashboardLeftMenuLogo/LayoutDashboardLeftMenuLogo';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { LayoutDashboardLeftMenuSettingsHeader } from './components/LayoutDashboardLeftMenuSettingsHeader/LayoutDashboardLeftMenuSettingsHeader';

type LayoutDashboardLeftMenuGroup = {
  variant?: 'settings';
  title?: string;
  items: LeftMenuItem[];
};

const getMenuItems = ({
  userRole,
  customerSlug,
  myPanelEnabled,
  isYouGovUser,
}: {
  userRole?: string;
  customerSlug?: string | null;
  myPanelEnabled: boolean;
  isYouGovUser: boolean;
}): LayoutDashboardLeftMenuGroup[] => {
  const youGovMenuItems: LeftMenuItem[] = [
    ...((getFFStore().hasFF('ff-profiles')
      ? [
          {
            name: 'YouGov AI Personas',
            to: '/audiences',
            icon: 'aiSearchLine',
          },
        ]
      : []) as LeftMenuItem[]),
    ...((getFFStore().hasFF('ff-flash-chat')
      ? [
          {
            name: 'Profiles AI Agent',
            to: '/ask-profiles',
            icon: 'ygAiLine',
          },
        ]
      : []) as LeftMenuItem[]),
  ];

  if (isYouGovUser) {
    return [
      {
        items: [
          {
            name: 'Home',
            to: '/home',
            icon: 'homeLineLine',
            exact: true,
          },
        ],
      },
      ...(youGovMenuItems.length > 0
        ? ([{ title: 'Products', items: youGovMenuItems }] as LayoutDashboardLeftMenuGroup[])
        : []),
    ];
  }

  const menuItems: LayoutDashboardLeftMenuGroup[] = [
    {
      variant: 'settings',
      items: [
        {
          name: 'Profile',
          to: '/settings/profile',
          icon: 'userCircleLine',
        },
        {
          name: 'Security',
          to: '/settings/security',
          icon: 'lockKeyholeSquareLine',
        },
        {
          name: 'Notifications',
          to: '/settings/notifications',
          icon: 'bell01Line',
        },
      ],
    },
    ...([
      {
        variant: 'settings',
        title: 'Features',
        items: [
          {
            name: 'Codeframes',
            to: '/settings/codeframes',
            icon: 'gitBranch01Line',
          },
        ],
      },
    ] as LayoutDashboardLeftMenuGroup[]),
    {
      variant: 'settings',
      title: 'Admin',
      items: [
        ...((userRole === 'administrator'
          ? [
              {
                name: 'Workspace',
                to: '/settings/workspace',
                icon: 'building05Line',
              },
            ]
          : []) as LeftMenuItem[]),
        {
          name: 'People',
          to: '/settings/users',
          icon: 'users02Line',
        },
        ...((userRole === 'administrator'
          ? [
              {
                name: 'Verified domains',
                to: '/settings/verified-domains',
                icon: 'server05Line',
              },
            ]
          : []) as LeftMenuItem[]),
        ...((userRole === 'administrator' && myPanelEnabled
          ? [
              {
                name: 'MyPanel',
                href: `${window.location.origin}/${customerSlug}/panels`,
                icon: 'building02Line',
              },
            ]
          : []) as LeftMenuItem[]),
        ...((userRole === 'administrator'
          ? [
              {
                name: 'Billing',
                to: '/settings/billing',
                icon: 'creditCard02Line',
              },
            ]
          : []) as LeftMenuItem[]),
      ],
    },
    {
      items: [
        {
          name: 'Home',
          to: '/home',
          icon: 'homeLineLine',
          exact: true,
        },
        {
          name: 'Projects',
          to: '/projects',
          icon: 'folderLine',
        },
        {
          name: 'Hey Yabble',
          to: '/hey-yabble',
          icon: 'generateLine',
        },
      ],
    },
    {
      title: 'Products',
      items: [
        {
          name: 'Gen',
          to: '/gen',
          icon: 'chatLine',
        },
        {
          name: 'Virtual Audiences',
          to: '/projects?type=va',
          icon: 'personaLine',
        },
        ...((getFFStore().hasFF('ff-profiles')
          ? [
              {
                name: 'YouGov AI Personas',
                to: '/audiences',
                icon: 'aiSearchLine',
              },
            ]
          : []) as LeftMenuItem[]),
        ...((getFFStore().hasFF('ff-flash-chat')
          ? [
              {
                name: 'Profiles AI Agent',
                to: '/ask-profiles',
                icon: 'ygAiLine',
              },
            ]
          : []) as LeftMenuItem[]),
        ...((getFFStore().hasFF('ff-stories')
          ? [
              {
                name: 'Stories',
                to: '/stories',
                icon: 'lineChartUp03Line',
              },
            ]
          : []) as LeftMenuItem[]),
        ...((userRole === 'administrator' && myPanelEnabled
          ? [
              {
                name: 'MyPanel',
                href: `/${customerSlug || 'default'}/panels`,
                icon: 'usersEditLine',
              },
            ]
          : []) as LeftMenuItem[]),
      ],
    },
  ];

  return menuItems;
};

export const LayoutDashboardLeftMenu: React.FC<{
  variant?: 'settings';
}> = ({ variant }) => {
  const userRole = useObservableState(getUserStore().$userRole);
  const customerSlug = useObservableState(getCustomerStore().$customerSlug);
  const myPanelEnabled = useObservableState(getBillingPlanStore().$myPanelEnabled, false);
  const isYouGovUser = useObservableEagerState(getFFStore().$hasFF('ff-restricted-yg-company'));
  const { isLeftMenuOpen, setLeftMenuState } = useLeftMenuOpen();

  const containerClasses = useClassNames({
    'is-open': isLeftMenuOpen,
    'is-settings': variant === 'settings',
  });

  useEffect(() => {
    if (isLeftMenuOpen) {
      document.body.classList.add('side-menu-hidden');
    } else {
      document.body.classList.remove('side-menu-hidden');
    }

    if (variant === 'settings') setLeftMenuState('open');
  }, [variant, isLeftMenuOpen, setLeftMenuState]);

  useEffect(() => {
    if (window.innerWidth <= 1024 && variant !== 'settings') setLeftMenuState('closed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups: LayoutDashboardLeftMenuGroup[] = useMemo(() => {
    const menuItems = getMenuItems({ userRole, customerSlug, myPanelEnabled, isYouGovUser });
    return menuItems.filter((item) =>
      variant === 'settings' ? item.variant === 'settings' : item.variant !== 'settings'
    );
  }, [userRole, customerSlug, myPanelEnabled, isYouGovUser, variant]);

  return (
    <S.Container
      className={containerClasses}
      backgroundColor="neutral0"
      padding={['paddingNone', 'paddingMD']}
    >
      <VStack stretch gap="gap4" className="left-menu-content" splitAfterIdx={1}>
        <S.Header fit="content" padding={['paddingSM', 'paddingNone']}>
          {variant === 'settings' ? (
            <LayoutDashboardLeftMenuSettingsHeader />
          ) : (
            <HStack stretch align="center" justify="space-between">
              <LayoutDashboardLeftMenuLogo />
              {isLeftMenuOpen && (
                <Box fit="content">
                  <PButtonIcon
                    icon="flexAlignLeftLine"
                    size="md"
                    onClick={() => setLeftMenuState('closed')}
                    className="layout-dashboard-left-menu-option-collapse"
                  />
                  <Tooltip
                    title="Collapse"
                    placement="bottom"
                    target=".layout-dashboard-left-menu-option-collapse"
                  />
                </Box>
              )}
            </HStack>
          )}
        </S.Header>
        <S.Scroller hideScrollbar>
          <VStack gap="gap4">
            {variant !== 'settings' && !isYouGovUser && <LayoutDashboardLeftMenuProjects />}
            <Box>
              {!isLeftMenuOpen && variant !== 'settings' && (
                <VStack gap="gap0">
                  <PButtonIcon
                    icon="flexAlignLeftLine"
                    size="md"
                    onClick={() => setLeftMenuState('open')}
                    className="layout-dashboard-left-menu-option-open"
                  />
                  <Tooltip title="Open" placement="right" target=".layout-dashboard-left-menu-option-open" />
                  <Box padding={['paddingMD', 'paddingNone']}>
                    <Divider color="neutral200" />
                  </Box>
                </VStack>
              )}
              {groups.map((group, i) => (
                <VStack key={i} gap="gap0">
                  {!!group.title &&
                    (isLeftMenuOpen ? (
                      <Box padding={['paddingMD', 'paddingSM', 'paddingXXS', 'paddingSM']}>
                        <Text color="neutral400" size="fontSizeXS" lineHeight="lineHeightSM" weight="medium">
                          {group.title}
                        </Text>
                      </Box>
                    ) : (
                      <Box padding={['paddingMD', 'paddingNone']}>
                        <Divider color="neutral200" />
                      </Box>
                    ))}
                  {group.items.map((item, j) => (
                    <LayoutDashboardLeftMenuItem key={j} item={item} variant={variant} />
                  ))}
                </VStack>
              ))}
            </Box>
          </VStack>
        </S.Scroller>
        {variant !== 'settings' && (
          <Box fit="content" padding={['paddingMD', 'paddingNone']}>
            <VStack gap="gap3">
              <LayoutDashboardLeftMenuSupport />
              <LayoutDashboardLeftMenuCustomers />
              <LayoutDashboardLeftMenuUser />
            </VStack>
          </Box>
        )}
      </VStack>
    </S.Container>
  );
};
